import * as angular from 'angular';

import { Injectable, Optional, SkipSelf } from '@angular/core';
import { downgradeInjectable } from "@angular/upgrade/static";

import { LocalDate } from '../types/local-date.type';
import { LocalDateTime } from '../types/local-date-time.type';
import { LocalTime } from '../types/local-time.type';

// IMPORTANT: Certaines fonction sont ecrites sous forme d'arrow function pour empecher les appelants de modifier le 'this'
// Il est important de les conserver sous cette forme tant que les fonctions continuent a etre injectes dans les vue angular-js

@Injectable({ providedIn: 'root' })
export class JSR310ConversionService {
	constructor(@Optional() @SkipSelf() previousInstance?: JSR310ConversionService) {
		if (previousInstance) {
			throw new Error(
				'JSR310ConversionService is already loaded. Import it in the AppModule only');
		}
	}

	private static pad(value: number): string {
		if (!value)
			return "00";
		return (value < 10 ? "0" : "") + value;
	}

	// Fonctions de serialisation de Date pour usage dans une URL (ex: /filtre?dateDebut=2010-01-01)

	public toLocalDateParam(this: JSR310ConversionService, date: Date): string {
		let year = date.getFullYear();
		let month = date.getMonth() + 1;
		let day = date.getDate();
		let localDate = year + "-" + JSR310ConversionService.pad(month) + "-" + JSR310ConversionService.pad(day);
		return localDate;
	}

	public toLocalDateParam2(this: JSR310ConversionService, date: LocalDate | LocalDateTime): string {
		let year = date[0];
		let month = date[1];
		let day = date[2];
		let localDate = year + "-" + JSR310ConversionService.pad(month) + "-" + JSR310ConversionService.pad(day);
		return localDate;
	}

	public toLocalTimeParam(this: JSR310ConversionService, date: Date): string {
		let hours = date.getHours();
		let minutes = date.getMinutes();
		let seconds = date.getSeconds();
		let localTime = JSR310ConversionService.pad(hours) + ":" + JSR310ConversionService.pad(minutes) + ":" + JSR310ConversionService.pad(seconds);
		return localTime;
	}

	public toLocalDateTimeParam: (date: Date) => string  = (date) => {
		return this.toLocalDateParam(date) + "T" + this.toLocalTimeParam(date);
	}

	// Fonctions de deserialisation de valeurs JSR310 inclues dans une reponse JSON en objet Date Javascript

	public fromLocalDate: (localDate: LocalDate | LocalDateTime | undefined) => Date | undefined = (localDate: LocalDate | LocalDateTime | undefined) =>  {
		return localDate ? this.fromMandatoryLocalDate(localDate) : undefined;
	}

	public fromNullableLocalDate: (localDate: LocalDate | number[] | LocalDateTime | null) => Date | null = (localDate: LocalDate | number[] | LocalDateTime | null) => {
		return localDate ? this.fromMandatoryLocalDate(localDate) : null;
	}
	
	public fromMandatoryLocalDate: (localDate: LocalDate | number[] | LocalDateTime) => Date = (localDate: LocalDate | number[] | LocalDateTime) => {
		let year = localDate[0];
		let month = localDate[1];
		let day = localDate[2];
		let date = new Date(year, month - 1, day);
		return date;
	}

	public fromLocalTime: (localTime: LocalTime | undefined) => Date | undefined = (localTime: LocalTime | undefined) => {
		return localTime ? this.fromMandatoryLocalTime(localTime) : undefined;
	}

	public fromNullableLocalTime: (localTime: LocalTime | null) => Date | null = (localTime: LocalTime| null) => {
		return localTime ? this.fromMandatoryLocalTime(localTime) : null;
	}

	public fromMandatoryLocalTime: (localTime: LocalTime) => Date = (localTime: LocalTime) => {
		let hours = localTime[0];
		let minutes = localTime[1];
		let seconds = localTime[2] ? localTime[2] : 0;
		let today = new Date();
		let date = new Date(today.getFullYear(), today.getMonth(), today.getDay(), hours, minutes, seconds);
		return date;
	}

	public fromLocalDateTime: (localDateTime: LocalDateTime | undefined) => Date | undefined = (localDateTime: LocalDateTime | undefined) => {
		return localDateTime ? this.fromMandatoryLocalDateTime(localDateTime) : undefined;
	}

	public fromNullableLocalDateTime: (localDateTime: LocalDateTime | null) => Date | null = (localDateTime: LocalDateTime| null) => {
		return localDateTime ? this.fromMandatoryLocalDateTime(localDateTime) : null;
	}

	public fromMandatoryLocalDateTime: (localDateTime: LocalDateTime) => Date = (localDateTime: LocalDateTime) => {
		let year = localDateTime[0];
		let month = localDateTime[1];
		let day = localDateTime[2];
		let hours = localDateTime[3];
		let minutes = localDateTime[4];
		let seconds = localDateTime[5] ? localDateTime[5] : 0;
		let date = new Date(year, month - 1, day, hours, minutes, seconds);
		return date;
	}

	// Fonctions de serialisation de Date pour incorporation dans un object JSON format le body d'un appel WEB Post ou Put

	public toLocalDate: (date: Date | undefined) => LocalDate | undefined = (date: Date | undefined) => {
		return date ? this.toMandatoryLocalDate(date) : undefined;
	}

	public toNullableLocalDate: (date: Date | null) => LocalDate | null = (date: Date | null) => {
		return date ? this.toMandatoryLocalDate(date) : null;
	}
	
	public toMandatoryLocalDate: (date: Date) => LocalDate = (date: Date) => {
		let year = date.getFullYear();
		let month = date.getMonth() + 1;
		let day = date.getDate();
		let localDate = [year, month, day] as LocalDate;
		return localDate;
	}
	
	public toLocalTime: (date: Date | undefined) => LocalTime | undefined = (date: Date | undefined) => {
		return date ? this.toMandatoryLocalTime(date) : undefined;
	}

	public toNullableLocalTime(date: Date | null): LocalTime | null {
		return date ? this.toMandatoryLocalTime(date) : null;
	}

	public toMandatoryLocalTime: (date: Date) => LocalTime = (date: Date) => {
		let hours = date.getHours();
		let minutes = date.getMinutes();
		let seconds = date.getSeconds();
	
		let localDate = [hours, minutes, seconds] as LocalTime;
		return localDate;
	}
	
	public toLocalDateTime: (date: Date | undefined) => LocalDateTime | undefined = (date: Date | undefined) => {
		return date ? this.toMandatoryLocalDateTime(date) : undefined;
	}

	public toNullableLocalDateTime(date: Date | null): LocalDateTime | null {
		return date ? this.toMandatoryLocalDateTime(date) : null;
	}

	public toMandatoryLocalDateTime: (date: Date) => LocalDateTime = (date: Date) => {
		let year = date.getFullYear();
		let month = date.getMonth() + 1;
		let day = date.getDate();
		let hours = date.getHours();
		let minutes = date.getMinutes();
		let seconds = date.getSeconds();
	
		let localDate = [year, month, day, hours, minutes, seconds] as LocalDateTime;
		return localDate;
	}

	public composeLocalDateTime: (date: Date, heure: Date) => LocalDateTime | undefined = (date: Date, heure: Date) => {
		if (!date || !heure)
			return undefined;

		let year = date.getFullYear();
		let month = date.getMonth() + 1;
		let day = date.getDate();
		let hours = heure.getHours();
		let minutes = heure.getMinutes();
		let localDateTime = [year, month, day, hours, minutes, 0] as LocalDateTime;
		return localDateTime;
	}
	
	public merge = (localDate: LocalDate, localTime: LocalTime) => {
		let year = localDate[0];
		let month = localDate[1];
		let day = localDate[2];
		let hours = localTime[0];
		let minutes = localTime[1];
		let seconds = localTime[2];
		let localDateTime = [year, month, day, hours, minutes, seconds] as LocalDateTime;
		return localDateTime;
	}
	
	// Fonctions de comparaison des localDates

	public equalsLocalDate: (date1: LocalDate | null, date2: LocalDate | null) => boolean = (date1: LocalDate | null, date2: LocalDate | null) => {
		if (date1 === null && date2 === null) {
			return true;
		}
		
		if (date1 === null || date2 === null) {
			return false;
		}
		
		return date1[0] === date2[0] && date1[1] === date2[1] && date1[2] === date2[2];
	}

	public compareLocalDate = (date1: LocalDate | null, date2: LocalDate | null , nullIsGreater?: boolean) => {
		if (date1 === null && date2 === null) {
			return 0;
		}
		
		if (date1 === null) {
			return nullIsGreater ? 1 : -1;
		}
		if (date2 === null) {
			return nullIsGreater ? -1 : 1;
		}
		
		let compare = date1[0] - date2[0];
		if (compare !== 0)
			return compare; 

		compare = date1[1] - date2[1];
		if (compare !== 0)
			return compare; 

		compare = date1[2] - date2[2];
		return compare; 
	}
}

angular
	.module('auroraApp')
	.service('jsr310ConversionService', downgradeInjectable(JSR310ConversionService));